import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { GardenPlant } from '../../services/gardenService';
import './Garden.css';

interface PlantNotePopoverProps {
  plant: GardenPlant;
  onClose: () => void;
  isReadOnly?: boolean;
}

const PlantNotePopover: React.FC<PlantNotePopoverProps> = ({
  plant,
  onClose,
  isReadOnly = false
}) => {
  const navigate = useNavigate();
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return 'Unknown date';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getEmojiDescription = (emoji: string | null, score: number | null | undefined): string => {
    if (!emoji) return 'No plant';
    if (score === null || score === undefined) return 'Plant';

    if (emoji === '🌸') return 'Small Flower (Score: 3-5)';
    if (emoji === '🌺') return 'Medium Flower (Score: 6-9)';
    if (emoji === '🌻') return 'Large Flower (Score: 10-15)';
    if (emoji === '🌳') return 'Tree (Score: 16+)';
    return 'Plant';
  };

  // Note: Activity breakdown would need to come from backend
  // For now, we show the score and date
  const activityScore = plant.activity_score || 0;

  const handleViewDayNote = () => {
    if (plant.plant_date) {
      // Format date to YYYY-MM-DD
      const date = new Date(plant.plant_date);
      const formattedDate = date.toISOString().split('T')[0];
      navigate(`/days/${formattedDate}`);
      onClose();
    }
  };

  return (
    <div className="garden-plant-modal-overlay">
      <div
        ref={modalRef}
        className="garden-plant-modal"
      >
        <div className="garden-plant-note-popover-header">
          <div className="garden-plant-note-popover-title">
            <span className="garden-plant-note-popover-emoji">{plant.emoji || '🌱'}</span>
            <span className="garden-plant-note-popover-plant-type">
              {getEmojiDescription(plant.emoji, plant.activity_score)}
            </span>
          </div>
          <button
            className="garden-plant-note-popover-close"
            onClick={onClose}
            title="Close"
          >
            ×
          </button>
        </div>

        <div className="garden-plant-note-popover-view">
          <div className="garden-plant-note-popover-content">
            <div className="garden-plant-activity-info">
              <div className="garden-plant-activity-date">
                <strong>Date:</strong> {formatDate(plant.plant_date)}
              </div>
              <div className="garden-plant-activity-score">
                <strong>Activity Score:</strong> {activityScore}
              </div>
              {plant.plant_date && (
                <div className="garden-plant-view-day-link">
                  <button
                    className="garden-btn garden-btn-primary"
                    onClick={handleViewDayNote}
                  >
                    View Day's Note
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlantNotePopover;
